"use client"

import type { MembersBoardBranchRow } from "@ibada/shared-types/overview"

import { HomeTile, HomeTileEmpty, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"
import { useTranslations } from "@/i18n"

/** Five branches fit the tile at its height; the rest are counted below. */
const MAX_ROWS = 5

/**
 * By branch (parish): each branch's members against the largest branch, with who joined this month. Answers
 * "where are our people" at a glance; the numbers are always printed, the bar only compares.
 */
export function BranchesTile({ branches, branchesLink }: { branches: MembersBoardBranchRow[]; branchesLink: HomeTileLink | null }) {
  const t = useTranslations("people.members.overview.board.branches")
  const largest = Math.max(0, ...branches.map((branch) => branch.total))
  const shown = branches.slice(0, MAX_ROWS)
  const hidden = branches.length - shown.length

  return (
    <HomeTile
      title={t("title")}
      subtitle={t(branches.length === 1 ? "subtitleOne" : "subtitleOther", { count: branches.length })}
      link={branchesLink}
      testId='members-board-branches'
    >
      {largest === 0 ? (
        <HomeTileEmpty message={t("empty")} />
      ) : (
        <>
          <ul className='flex flex-col gap-2'>
            {shown.map((branch) => (
              <li key={branch.branchId} data-testid='branch-row' className='flex flex-col gap-1'>
                <span className='flex items-baseline gap-2 text-[12.5px]'>
                  <span className='truncate font-bold'>{branch.name}</span>
                  {branch.newThisMonth > 0 ? (
                    <span className='shrink-0 text-[11px] font-bold text-emerald-700 dark:text-emerald-400'>
                      {t("newThisMonth", { count: branch.newThisMonth })}
                    </span>
                  ) : null}
                  <span className='ml-auto font-bold tabular-nums'>{branch.total}</span>
                </span>
                <span className='flex h-1.5 overflow-hidden rounded-full bg-muted' aria-hidden>
                  {branch.total > 0 ? (
                    <span className='rounded-full bg-emerald-600 dark:bg-emerald-500' style={{ width: `${(branch.total / largest) * 100}%` }} />
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
          {hidden > 0 ? (
            <span className='mt-auto text-[11.5px] text-muted-foreground'>
              {t(hidden === 1 ? "moreOne" : "moreOther", { count: hidden })}
            </span>
          ) : null}
        </>
      )}
    </HomeTile>
  )
}
